import { FunctionsHttpError } from "@supabase/supabase-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDebouncedValue } from "@mantine/hooks";
import { STAFF_ELSEWHERE_SENTENCE, isStaffElsewhere } from "@how/app-kit";
import type { Enums } from "@how/database";
import { z } from "zod";
import { supabase } from "../../lib/supabase";

/**
 * Creating a church and managing its owners from the control plane (ADR 0064). Platform super admins only: the
 * database refuses everyone else (create_tenant_with_owner_email, platform_slug_status, platform_church_logins), and
 * invite-member refuses anyone who is neither a super admin nor holds users.manage in the church.
 */

// ------------------------------------------------------------------------------------------------ inviting

/** A refusal from invite-member, with its error code kept so the screen can put it in words. */
export type InviteError = Error & { code: string };

interface InviteBody {
  readonly tenantId: string;
  readonly email: string;
  readonly resend?: boolean;
}

/**
 * Calls invite-member as the signed-in super admin: the address is invited as the church's owner, with the link
 * opening the church's staff dashboard. With resend, the email goes again to an address still invited.
 */
export async function inviteOwner({ tenantId, email, resend = false }: InviteBody): Promise<void> {
  const result = await supabase.functions.invoke<{ status?: string }>("invite-member", {
    body: {
      tenant_id: tenantId,
      email: email.trim(),
      role_keys: ["tenant_owner"],
      surface: "staff_dashboard",
      ...(resend ? { resend: true } : {}),
    },
  });
  const failure: unknown = result.error;
  if (failure === null) return;
  let code = "";
  let message = failure instanceof Error ? failure.message : "";
  if (failure instanceof FunctionsHttpError) {
    const context: unknown = failure.context;
    const body =
      context instanceof Response
        ? ((await context.json().catch(() => null)) as {
            error?: { code?: string; message?: string };
          } | null)
        : null;
    code = body?.error?.code ?? "";
    message = body?.error?.message ?? message;
  }
  throw Object.assign(new Error(message), { code });
}

/** What invite-member refused, in words DCX can act on. */
export function describeInviteError(error: unknown): string {
  // One staff church per login (ADR 0068): the one sentence, whichever screen asked.
  if (isStaffElsewhere(error)) return STAFF_ELSEWHERE_SENTENCE;
  const e = (error ?? {}) as { code?: string; message?: string };
  switch (e.code) {
    case "already_member":
      return "This address already has a login in this church. Find it in the list.";
    case "forbidden":
      if (/support session/.test(e.message ?? ""))
        return "End your support session on this church first: a support session cannot invite people.";
      return "Only a platform super admin, with two-step verification, can invite a church's owner.";
    case "no_surface_host":
      return "This church has no active staff dashboard address, so the invitation link has nowhere to go.";
    case "invalid_request":
      return "Check the email address.";
    case "not_invited":
      return "This address has no open invitation in this church. Invite it instead.";
    case "too_soon":
      return "An email went to this address a moment ago. Try again in a minute.";
    case "unauthenticated":
      return "Your session has ended. Sign in again.";
    default:
      return "The invitation was not sent. Try again.";
  }
}

// ------------------------------------------------------------------------------------------------ the address check

/** tenants_slug_format, as the database writes it. Reserved and taken are the database's own answer, not this one. */
export const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/;

export type SlugStatus = "available" | "invalid" | "reserved" | "taken";
const slugStatusSchema = z.enum(["available", "invalid", "reserved", "taken"]);

export interface SlugCheck {
  /** The address is well formed and its answer is not in yet (typing, or the question in flight). */
  readonly checking: boolean;
  /** The database's answer for the address as it is now; null while checking or before it is well formed. */
  readonly status: SlugStatus | null;
  /** The question failed; the address is checked again on create. */
  readonly failed: boolean;
}

/**
 * The live check under the address field: the database's own answer (the reserved list and the addresses taken live
 * there), asked 300 ms after typing stops, and only for an address that passes the format rule.
 */
export function useSlugStatus(slug: string): SlugCheck {
  const current = slug.trim().toLowerCase();
  const [debounced] = useDebouncedValue(current, 300);
  const wellFormed = SLUG_PATTERN.test(current);
  const query = useQuery({
    queryKey: ["slug-status", debounced],
    enabled: SLUG_PATTERN.test(debounced),
    staleTime: 5_000,
    queryFn: async (): Promise<SlugStatus> => {
      const { data, error } = await supabase.rpc("platform_slug_status", { p_slug: debounced });
      if (error) throw error;
      return slugStatusSchema.parse(data);
    },
  });
  const caughtUp = debounced === current && !query.isFetching;
  return {
    checking: wellFormed && !caughtUp,
    status: wellFormed && caughtUp && query.isSuccess ? query.data : null,
    failed: wellFormed && caughtUp && query.isError,
  };
}

// ------------------------------------------------------------------------------------------------ creating a church

export interface CreateChurchInput {
  readonly name: string;
  readonly slug: string;
  readonly plan: Enums<"plan_key">;
  readonly timeZone: string;
  readonly countryCode: string;
  readonly locale: string;
  readonly ownerEmail: string;
}

const createdSchema = z.object({
  tenant_id: z.string(),
  slug: z.string(),
  name: z.string(),
  owner_email: z.string(),
  owner: z.enum(["active", "invitation_needed"]),
  hosts: z.object({
    public_web: z.string(),
    staff_dashboard: z.string(),
    member_portal: z.string(),
  }),
});

export interface CreatedChurch {
  readonly tenantId: string;
  readonly name: string;
  readonly ownerEmail: string;
  readonly hosts: { readonly site: string; readonly dash: string; readonly members: string };
  /**
   * What happened to the owner: an existing account made the active owner at once; a new address invited (the
   * invitation email is on its way); or invited but the invitation failed, with the reason in words.
   */
  readonly owner:
    | { readonly kind: "active" }
    | { readonly kind: "invited" }
    | { readonly kind: "invite_failed"; readonly reason: string };
}

/** A refusal from create_tenant_with_owner_email, with its SQLSTATE kept. */
export type CreateChurchError = Error & { code: string | undefined };

/**
 * Creates the church (create_tenant_with_owner_email, one transaction, as the signed-in super admin) and, when the
 * owner's address has no account, invites it through invite-member. The church exists even when the invitation
 * fails; the result says so, and the church's page can send it again.
 */
export function useCreateChurch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateChurchInput): Promise<CreatedChurch> => {
      const { data, error } = await supabase.rpc("create_tenant_with_owner_email", {
        p_slug: input.slug.trim().toLowerCase(),
        p_name: input.name.trim(),
        p_owner_email: input.ownerEmail.trim(),
        p_plan: input.plan,
        p_time_zone: input.timeZone,
        p_country_code: input.countryCode.trim().toUpperCase(),
        p_locale: input.locale.trim(),
      });
      if (error) {
        throw Object.assign(new Error(error.message), { code: error.code }) as CreateChurchError;
      }
      const made = createdSchema.parse(data);
      const base = {
        tenantId: made.tenant_id,
        name: made.name,
        ownerEmail: made.owner_email,
        hosts: {
          site: made.hosts.public_web,
          dash: made.hosts.staff_dashboard,
          members: made.hosts.member_portal,
        },
      };
      if (made.owner === "active") return { ...base, owner: { kind: "active" } };
      try {
        await inviteOwner({ tenantId: made.tenant_id, email: made.owner_email });
        return { ...base, owner: { kind: "invited" } };
      } catch (failure) {
        return { ...base, owner: { kind: "invite_failed", reason: describeInviteError(failure) } };
      }
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["tenants"] });
      await client.invalidateQueries({ queryKey: ["platform-stats"] });
    },
  });
}

/** What create_tenant_with_owner_email refused, in words. A broken rule carries its own sentence from the database. */
export function describeCreateError(error: unknown): string {
  // One staff church per login (ADR 0068): the one sentence, whichever screen asked.
  if (isStaffElsewhere(error)) return STAFF_ELSEWHERE_SENTENCE;
  const e = (error ?? {}) as { code?: string; message?: string };
  const message = e.message ?? "";
  switch (e.code) {
    case "23505":
      return "That address is already taken. Choose another.";
    case "23514":
      return message.length > 0
        ? message.charAt(0).toUpperCase() + message.slice(1) + "."
        : "Something in the form breaks a rule. Check it and try again.";
    case "42501":
      return "Only a platform super admin, with two-step verification, can create a church.";
    default:
      return "The church was not created. Try again.";
  }
}

// ------------------------------------------------------------------------------------------------ a church's logins

export interface ChurchLogin {
  readonly membershipId: string;
  readonly email: string;
  readonly displayName: string;
  readonly status: Enums<"membership_status">;
  readonly roles: readonly string[];
  readonly invitedAt: string;
  readonly acceptedAt: string | null;
  readonly signedIn: boolean;
}

/** A church's invited, active and suspended logins, owners first (platform_church_logins). */
export function useChurchLogins(tenantId: string, enabled = true) {
  return useQuery({
    queryKey: ["church-logins", tenantId],
    enabled,
    queryFn: async (): Promise<ChurchLogin[]> => {
      const { data, error } = await supabase.rpc("platform_church_logins", {
        p_tenant_id: tenantId,
      });
      if (error) throw error;
      return data.map((row) => ({
        membershipId: row.membership_id,
        email: row.email,
        displayName: row.display_name,
        status: row.status,
        roles: row.roles,
        invitedAt: row.invited_at,
        acceptedAt: row.accepted_at,
        signedIn: row.signed_in,
      }));
    },
  });
}

/** Invites an owner, or sends an invited owner's email again; the list reloads either way. */
export function useInviteOwner(tenantId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { readonly email: string; readonly resend?: boolean }) =>
      inviteOwner({ tenantId, email: input.email, resend: input.resend ?? false }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["church-logins", tenantId] });
    },
  });
}
